// =============================================================================
// AGENT 2 — MISTRZ UI: SLOT PRACOWNIKA
// Drop target — przyjmuje TaskCard metodą HTML5 DnD i Touch API.
// =============================================================================

import './WorkerSlot.css';
import type { Worker, Task } from '../../types/models';

const WORKER_BADGE_ICONS: Record<string, string[]> = {
  w1: ['assets/icons/skill-hydraulics.svg', 'assets/icons/skill-domofony.svg', 'assets/icons/skill-ogrod.svg'],
  w2: ['assets/icons/skill-domofony.svg', 'assets/icons/skill-electric.svg', 'assets/icons/skill-szlabany.svg'],
  w3: ['assets/icons/skill-okna.svg', 'assets/icons/skill-cleaning.svg', 'assets/icons/skill-bramy.svg'],
  w4: ['assets/icons/skill-windy.svg', 'assets/icons/skill-ventilation.svg', 'assets/icons/skill-cleaning.svg'],
};

interface WorkerSlotProps {
  worker: Worker;
  assignedTask: Task | null;
  taskCount: number;
  isDragOver: boolean;
  isTaskSelected: boolean;
  dropFeedback: 'success' | 'error' | null;
  onDragOver: (e: React.DragEvent, workerId: string) => void;
  onDragLeave: () => void;
  onDrop: (workerId: string) => void;
  onTapAssign: (workerId: string) => void;
}

export function WorkerSlot({
  worker,
  assignedTask,
  taskCount,
  isDragOver,
  isTaskSelected,
  dropFeedback,
  onDragOver,
  onDragLeave,
  onDrop,
  onTapAssign,
}: WorkerSlotProps) {
  const workloadPercentage = Math.min((taskCount / 5) * 100, 100);
  const isOverloaded = taskCount > 5;

  return (
    <div
      className={[
        'worker-slot',
        isDragOver && 'worker-slot--drag-over',
        assignedTask && 'worker-slot--assigned',
        dropFeedback === 'success' && 'worker-slot--feedback-success',
        dropFeedback === 'error' && 'worker-slot--feedback-error',
      ]
        .filter(Boolean)
        .join(' ')}
      data-worker-id={worker.id}
      onDragOver={(e) => { e.preventDefault(); onDragOver(e, worker.id); }}
      onDragLeave={onDragLeave}
      onDrop={() => onDrop(worker.id)}
      onClick={() => onTapAssign(worker.id)}
      aria-label={`Pracownik: ${worker.name}`}
      aria-dropeffect="move"
    >
      <div className="worker-slot__header">
        <img
          className="worker-slot__avatar"
          src={worker.avatarAsset}
          alt={worker.name}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div className="worker-slot__name-wrap">
          <div className="worker-slot__name">{worker.name}</div>
        </div>
      </div>

      <div className="worker-slot__section">
        <div className="worker-slot__section-label">Obłożenie:</div>
        <div
          className="worker-slot__workload"
          aria-label={`Obłożenie: ${taskCount} z 5 zgłoszeń`}
          data-overloaded={isOverloaded}
        >
          <div
            className="worker-slot__workload-fill"
            style={{ width: `${workloadPercentage}%` }}
            data-overloaded={isOverloaded}
          />
        </div>
      </div>

      <div className="worker-slot__section worker-slot__section--skills">
        <div className="worker-slot__section-label">Umiejętności:</div>
        <div className="worker-slot__skills">
          {(WORKER_BADGE_ICONS[worker.id] ?? []).map((iconPath, index) => (
            <span key={`${worker.id}-${index}`} className="worker-slot__skill-chip" title={worker.name} aria-hidden>
              <img className="worker-slot__skill-icon" src={iconPath} alt="" />
            </span>
          ))}
        </div>
      </div>

      {isDragOver || isTaskSelected ? (
        <div className="worker-slot__drop-hint" aria-hidden>
          {isDragOver ? 'Upuść tutaj' : 'DOTKNIJ/UPUŚĆ'}
        </div>
      ) : null}
    </div>
  );
}
